import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../theme.js';
import Button from './Button.js';
import { AlertCircle, Eye, EyeOff } from './Icons/index.js';
const Input = (props) => {
    const ref = useRef(null);
    const inputRef = useRef(null);
    const lang = useSelector((s) => s.lang);
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (props.autofocus && inputRef && inputRef.current) {
            // @ts-ignore
            inputRef.current?.focus();
        }
    }, [inputRef]);
    useEffect(() => {
        if (props.inputRef) {
            props.inputRef.current = inputRef.current;
        }
    }, []);
    const button = props.onSubmit
        ? {
            onClick: async () => {
                if (props.onSubmit) {
                    setLoading(true);
                    await props.onSubmit();
                    setLoading(false);
                }
            },
            value: lang.inputs.save,
            theme: 'secondary',
            size: undefined,
            tabIndex: undefined,
            style: {},
            loading: loading,
        }
        : props.button;
    return (React.createElement(InpContainer, { readOnly: props.readonly, style: props.containerStyle, className: "iris-input-container" },
        React.createElement("label", null,
            props.validationErrors && !!props.validationErrors.length && (React.createElement(ValidationErrorsWrapper, { className: "validation-errors" }, props.validationErrors.map((validationError) => (React.createElement(ValidationError, null,
                React.createElement(AlertCircle, { style: { margin: '.1em 0.2em -.1em 0', color: THEME.WARNING } }),
                ' ',
                validationError))))),
            props.success && React.createElement(Success, null),
            React.createElement(Inp, { ...props.aria, onClick: props.onClick, id: props.id, sensitive: props.sensitive, onChange: props.onChange, onKeyDown: props.onSubmit
                    ? async (e) => {
                        if (e.key === 'Enter' && props.onSubmit) {
                            setLoading(true);
                            await props.onSubmit(props.value);
                            setLoading(false);
                        }
                    }
                    : props.onKeyDown, className: props.className, pattern: props.pattern, required: props.required, type: props.sensitive && !visible ? 'password' : props.type, step: props.step, min: props.min, max: props.max, value: props.static || props.value, style: {
                    padding: props.placeholder ? '' : '20px 15px',
                    ...props.style,
                    paddingRight: (() => {
                        const element = ref && ref.current;
                        if (element) {
                            const rect = element.getBoundingClientRect();
                            return rect && rect.width && `${rect.width + 15}px`;
                        }
                    })(),
                }, ref: inputRef, onFocus: props.onFocus, onBlur: props.onBlur, disabled: props.disabled, readOnly: props.readonly, autoComplete: props.autoComplete, maxLength: props.maxLength }),
            React.createElement("span", { className: `inp-placeholder ${props.value || props.value === 0 ? 'active' : ''}` },
                props.placeholder,
                props.required ? '*' : ''),
            button && (React.createElement(Button, { onClick: button.onClick, loading: button.loading, disabled: button.disabled, value: button.value, tabIndex: button.tabIndex, innerRef: ref, theme: button.theme || 'secondary', size: button.size || 'small', style: {
                    ...button.style,
                    position: 'absolute',
                    right: '2px',
                    top: '2px',
                } }))),
        props.embed ? React.createElement(Embed, null, props.embed) : null,
        props.sensitive && (React.createElement(EyeBtn, { onClick: (e) => {
                e.preventDefault();
                setVisible(!visible);
            } }, visible ? React.createElement(EyeOff, null) : React.createElement(Eye, null)))));
};
export default Input;
const Success = styled.div `
  background-color: #25c525;
  position: absolute;
  height: 5px;
  width: 5px;
  right: 5px;
  top: 5px;
  border-radius: 100%;
`;
const InpContainer = styled.div `
  position: relative;
  font-weight: 700;

  .inp-placeholder {
    position: absolute;
    top: ${(props) => (props.readOnly ? `-2px !important` : `14px`)};
    left: ${(props) => (props.readOnly ? `0px` : `15px`)};
    opacity: 0.6;
    font-size: ${(props) => (props.readOnly ? `0.8em !important` : `0.8em`)};
    transition: all 0.2s;
    max-height: 1.3em;
    overflow: hidden;
    pointer-events: none;
  }

  .inp-placeholder.active {
    top: 7px;
    font-size: 0.5em;
  }

  .plus-minus-btn {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 20px;
    width: 60px;
    background-color: ${THEME.BACKGROUND};
    border: 1px solid
      rgba(204, 204, 204, ${(props) => (props.readOnly ? `0` : `1`)});
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
    cursor: pointer;
    user-select: none;

    & > span {
      position: absolute;
      top: 0;
      bottom: 0;
      width: 30px;

      & > div {
        text-align: center;
        line-height: 35px;
      }
    }

    & > span:first-child {
      border-right: 1px solid
        rgba(204, 204, 204, ${(props) => (props.readOnly ? `0` : `1`)});
      left: 0;
    }

    & > span:last-child {
      right: 0;
    }
  }

  &:focus-within {
    .validation-errors {
      display: none !important;
    }
  }
`;
const Inp = styled.input `
  outline: none;
  border-radius: 3px;
  background-color: ${(props) => props.readOnly ? `transparent` : THEME.COLOR_SECONDARY};
  padding: ${(props) => props.readOnly ? `15px 0px 5px 0px` : `15px 15px 5px 15px`};
  transition: all 0.2s;
  font-size: 0.9em;
  margin-bottom: 20px;
  width: 100%;
  background-image: none;
  border: 1px solid
    rgba(204, 204, 204, ${(props) => (props.readOnly ? `0` : `1`)});
  box-sizing: border-box;

  &:disabled {
    color: ${THEME.TEXT_MUTED};
    background-color: ${THEME.FILL};
  }

  &:focus ~ .inp-placeholder {
    top: 7px;
    font-size: 0.5em;
  }
`;
const EyeBtn = styled.div `
  position: absolute;
  right: 1px;
  top: 10px;

  width: 30px;
  height: 30px;
  cursor: pointer;
  user-select: none;

  & > div {
    text-align: center;
    line-height: 35px;
  }
`;
const Embed = styled.div `
  position: absolute;
  right: 10px;
  top: 10px;

  height: 30px;
  font-size: 0.9em;
  font-weight: normal;
`;
const ValidationErrorsWrapper = styled.div `
  position: absolute;
  left: 0px;
  top: -27px;
  width: 100%;
`;
const ValidationError = styled.div `
  border-radius: 3px;
  background: ${THEME.COLOR_SECONDARY};
  display: inline-block;
  font-size: 0.7em;
  padding: 4px 7px;
  box-shadow: 0 20px 70px -30px rgba(0, 0, 0, 0.15);
  margin-right: 6px;
  border: 1px solid ${THEME.BORDER_STRONG};
`;
//# sourceMappingURL=Input.js.map